import { isEqual } from 'lodash';
import { useEffect, useMemo } from 'react';

import { Declarant } from '@e-origin/shared';

import { ListFilterChangeEvent, ListFilterDropdown, ListFilterDropdownElements } from '@components';
import { useUser } from '@hooks/user';
import { useSettings } from '@hooks/useSettings';
import { useUsersByDeclarant } from '@hooks/useUsersByDeclarant';

export interface UserVisibilityProps {
  declarant: Declarant;
  userFilters: string[];
  onChange(users: string[]): void;
}

export const UserVisibility: React.FC<UserVisibilityProps> = ({ declarant, userFilters, onChange }) => {
  const { data: user } = useUser();
  const isSupervisor = user?.isSupervisor;
  const { data: settings } = useSettings();
  const { data: usersList } = useUsersByDeclarant({ declarantId: isSupervisor || !settings.isForwarder ? declarant._id : null });

  useEffect(() => {
    if (!settings?.isForwarder) {
      return;
    }

    if (user?._id && !isSupervisor) {
      onChange([user._id]);
    }
  }, [user, isSupervisor]);

  const usersDropdownOptions = useMemo(() => {
    const usersDropdownOptionList: ListFilterDropdownElements = {};

    usersList.forEach((usr) => {
      usersDropdownOptionList[usr._id] = {
        label: `${usr.firstName} ${usr.lastName}`,
        checked: !!userFilters.find((userIdFromCriteria) => userIdFromCriteria === usr._id),
      };
    });

    return usersDropdownOptionList;
  }, [usersList, userFilters]);

  const filterByUserHandler = (userOption: ListFilterChangeEvent) => {
    if (!usersList.length) {
      return;
    }

    let filterByUsers: string[];
    if (!userOption.id) {
      if (userOption.checked) {
        filterByUsers = usersList.map((usr) => usr._id);
      } else {
        filterByUsers = [];
      }
    } else {
      filterByUsers = usersList.reduce((acc, cur) => {
        if ((userOption.id === cur._id && userOption.checked) || (userFilters.includes(cur._id) && userOption.id !== cur._id)) {
          acc.push(cur._id);
        }
        return acc;
      }, []);
    }

    if (!isEqual(filterByUsers, userFilters)) {
      onChange(filterByUsers);
    }
  };

  if (isSupervisor || !settings?.isForwarder) {
    return <ListFilterDropdown title={'Users visibility'} values={usersDropdownOptions} onChange={filterByUserHandler} />;
  } else {
    return <></>;
  }
};
